import React, {PureComponent} from 'react';
import styled from 'styled-components';
import Tree from 'react-sortable-tree';
import {NotificationManager} from 'react-notifications';

import {withUser} from '../../lib/user';
import * as api from '../../lib/api';
import Loading from '../../components/Loading';
import Error from '../../components/Error';
import Screen from '../../components/Screen';
import Content from '../../components/Content';
import SubHeader from '../../components/SubHeader';
import Button from '../../components/Button';
import Placeholder from '../../components/Placeholder';
import Icon from '../../components/Icon';
import Input from '../../components/Input';
import Card from '../../components/Card';
import Menu from '../../components/Menu';
import search from '../../lib/search';

import CatalogTypeForm from './CatalogTypeForm';
import CatalogItemForm from './CatalogItemForm';
import CatalogMetadataForm from './CatalogMetadataForm';

import 'react-sortable-tree/style.css';
import './style.css';

class CatalogScreen extends PureComponent {
  state = {
    types: [],
    tree: [],
    loading: true,
    failed: false,
    catalogDialog: {
      open: false,
      parent: null,
      item: null
    },
    typeDialog: {
      open: false,
      type: null
    },
    metadataDialog: {
      open: false,
      item: null
    },
    expanded: {},
    searchTerm: '',
    attributesOnly: false,
    copyMenuNodeId: null,
    canCopy: true
  };

  toggleCopyMenu = id => {
    this.setState(state => ({
      copyMenuNodeId: state.copyMenuNodeId === id ? null : id
    }));
  }

  closeCopyMenu = () => {
    this.setState({copyMenuNodeId: null});
  }

  handleSearchChange = e => {
    this.setState({searchTerm: e.target.value});
  }

  handleAttributesOnlyChange = e => {
    this.setState({attributesOnly: e.target.value === 'withAttributes'});
  }

  isFiltering() {
    const {searchTerm, attributesOnly} = this.state;
    return searchTerm.trim() !== '' || attributesOnly;
  }

  async componentDidMount() {
    const {credentials} = this.props;

    try {
      const [catalog, types] = await Promise.all([
        api.getCatalog(credentials),
        api.getCatalogTypes(credentials)
      ]);

      this.setState({tree: this.buildCatalogTree(catalog), types});
    }
    catch(e) {
      this.setState({failed: true});
    }
    finally {
      this.setState({loading: false});
    }
  }

  openItemDialog = (item, parent) => {
    this.setState({catalogDialog: {open: true, loading: false, item, parent}});
  }

  closeItemDialog = () => {
    this.setState({catalogDialog: {open: false, loading: false, item: null, parent: null}});
  }

  openTypeDialog = type => {
    this.setState({typeDialog: {open: true, loading: false, type}});
  }

  closeTypeDialog = () => {
    this.setState({typeDialog: {open: false, loading: false, type: null}});
  }

  openMetadataDialog = item => {
    this.setState({metadataDialog: {open: true, item}});
  }

  closeMetadataDialog = () => {
    this.setState({metadataDialog: {open: false, item: null}});
  }

  handleSaveMetadata = async item => {
    const {credentials} = this.props;

    try {
      const saved = await api.editCatalogItem(item, credentials);

      this.setState(state => ({
        tree: this.buildCatalogTree([
          ...flattenTree(state.tree).filter(i => i.id !== saved.id),
          saved
        ])
      }));
      this.closeMetadataDialog();
      NotificationManager.success(`Attributes for "${saved.name}" were saved`);
      return true;
    }
    catch(e) {
      NotificationManager.error(`Unable to save attributes for "${item.name}". Please try again`);
      return false;
    }
  }

  deleteItem = async item => {
    // eslint-disable-next-line
    if (!confirm(`Are you sure you want to delete the catalog item "${item.name}"?`)) {
      return;
    }

    const {credentials} = this.props;
    
    this.setState(state => ({
      tree: this.buildCatalogTree(flattenTree(state.tree).filter(i => i.id !== item.id))
    }));

    api.deleteCatalogItem(item, credentials);
  }

  copyItem = async item => {
  const {credentials} = this.props;

  try {
    const copy = await api.createCatalogItem({
      ...item,
      tmp: true,
      name: `Copy of ${item.name}`
    }, credentials);

    this.setState(state => ({
      tree: this.buildCatalogTree([
        ...flattenTree(state.tree),
        copy
      ])
    }));
    NotificationManager.success(`"${item.name}" was copied`);
  }
  catch(e) {
    NotificationManager.error(`Unable to copy "${item.name}". Please try again`);
  }
}

  // Recursively copy each item from a catalog
  // Creates "Copy of (name)" for parent item but not for children
  // Copies are collected in the caller's `created` array as they come back
  // If one item fails copying we keep the others in the created array and merge them in after
  copyItemTree = async (item, parentId, created, isRoot = true) => {
    const {credentials} = this.props;

    const copy = await api.createCatalogItem({
      ...item,
      tmp: true,
      parentId,
      name: isRoot ? `Copy of ${item.name}` : item.name
    }, credentials);

    created.push(copy);
    for (const child of item.children || []) {
      await this.copyItemTree(child, copy.id, created, false);
    }
  }

  handleCopyTree = async parent => {
    // Stop copying ability until the copy tree is finished
    this.setState({canCopy: false});
    const created = [];

    try {
      await this.copyItemTree(parent, parent.parentId, created);
      NotificationManager.success(`"${parent.name}" was copied`);
    }
    catch(e) {
      NotificationManager.error(created.length > 0
        ? `"${parent.name}" was only partly copied. Please review the copies and try again`
        : `Unable to copy "${parent.name}". Please try again`);
    }
    finally {
      // merge in whatever was succesfully copied
      this.setState(state => ({
        canCopy: true,
        tree: created.length > 0
          ? this.buildCatalogTree([...flattenTree(state.tree), ...created])
          : state.tree
      }));
    }
  }

  handleExpandCollapse = ({ node, expanded }) => {
    this.setState(prevState => ({
      expanded: {
        ...prevState.expanded,
        [node.id]: expanded
      }
    }));
  }

  handleSave = async item => {
    const {credentials} = this.props;

    try {
      const method = item.tmp ? 'createCatalogItem' : 'editCatalogItem';
      item = await api[method](item, credentials);

      this.setState(state => ({
        tree: this.buildCatalogTree([
          ...flattenTree(state.tree).filter(i => i.id !== item.id),
          item
        ])
      }));
      this.closeItemDialog();
      return true;
    }
    catch(e) {
      NotificationManager.error('Unable to save catalog item. Please try again');
      return false;
    }
  }

  handleChangeTree = tree => {
    const {credentials} = this.props;
    const normalized = tree.map(root => normalizeTree(root));

    // While filtering, the rendered tree is only a partial view (matches plus
    // their ancestors). Sibling order is never persisted, so the only thing a
    // drag move changes is the parent links among the visible items.
    if (this.isFiltering()) {
      const newParentById = flattenTree(normalized).reduce((map, item) => {
        map[item.id] = item.parentId;
        return map;
      }, {});

      const merged = flattenTree(this.state.tree).map(item =>
        item.id in newParentById ? {...item, parentId: newParentById[item.id]} : item
      );

      api.organizeCatalog(merged, credentials);
      this.setState({tree: this.buildCatalogTree(merged)});
      return;
    }

    // Dispatch the update tree call, but optimistically ignore the result.
    api.organizeCatalog(flattenTree(normalized), credentials);
    this.setState({tree: normalized});
  }

  handleSaveType = async type => {
    const {credentials} = this.props;

    try {
      const method = type.tmp ? 'createCatalogType' : 'editCatalogType';
      type = await api[method](type, credentials);

      this.setState(state => ({
        types: [
          ...state.types.filter(t => t.id !== type.id),
          type
        ],
        tree: this.buildCatalogTree(flattenTree(state.tree).map(item => item.type.id === type.id ? {...item, type} : item))
      }));
      this.closeTypeDialog();
      return true;
    }
    catch(e) {
      NotificationManager.error('Unable to save catalog item. Please try again');
      return false;
    }
  }

  buildCatalogTree(catalog) {
    return catalog
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter(item => !item.parentId).map(item => this.buildTreeFromNode(item, catalog));
  }
  
  buildTreeFromNode(item, catalog, forceExpanded = false) {
    return {
      ...item,
      title: item.name,
      subtitle: item.type.name,
      expanded: forceExpanded || this.state.expanded[item.id] || false,
      children: getChildren(item, catalog).map(child => this.buildTreeFromNode(child, catalog, forceExpanded))
    };
  }

  getDisplayTree() {
    const {tree, searchTerm, attributesOnly} = this.state;

    if (!this.isFiltering()) {
      return tree;
    }

    const flat = flattenTree(tree);
    const byId = flat.reduce((map, item) => {
      map[item.id] = item;
      return map;
    }, {});
    const term = searchTerm.trim();

    const matches = flat.filter(item => {
      const matchesTerm = !term || search(item.name, term) || (item.type && search(item.type.name, term));
      return matchesTerm && (!attributesOnly || hasAttributes(item));
    });

    // Keep every ancestor of a match so the hierarchy leading to it stays visible.
    const includedIds = new Set();
    matches.forEach(item => {
      includedIds.add(item.id);
      let parentId = item.parentId;
      while (parentId != null && byId[parentId] && !includedIds.has(parentId)) {
        includedIds.add(parentId);
        parentId = byId[parentId].parentId;
      }
    });

    // Keep every descendant of a match so its full contents stay visible.
    const childrenByParentId = flat.reduce((map, item) => {
      if (item.parentId != null) {
        (map[item.parentId] = map[item.parentId] || []).push(item);
      }
      return map;
    }, {});
    const descendantQueue = matches.map(item => item.id);
    while (descendantQueue.length > 0) {
      const children = childrenByParentId[descendantQueue.pop()] || [];
      children.forEach(child => {
        if (!includedIds.has(child.id)) {
          includedIds.add(child.id);
          descendantQueue.push(child.id);
        }
      });
    }

    const filtered = flat.filter(item => includedIds.has(item.id));
    return filtered
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter(item => !item.parentId)
      .map(item => this.buildTreeFromNode(item, filtered, true));
  }

  renderCatalog() {
    const {loading, failed, types, catalogDialog, typeDialog, metadataDialog, searchTerm, attributesOnly, copyMenuNodeId, canCopy} = this.state;

    if(loading) {
      return <Loading fullScreen message="Loading catalog..." />;
    }

    if(failed) {
      return <Error fullScreen message="Unable to load the catalog" />;
    }

    const displayTree = this.getDisplayTree();

    return (
      <Content>
        <SubHeader options={<Button onClick={() => this.openTypeDialog()}>Add a Type</Button>}>Catalog Types</SubHeader>
        <Description>Catalog types are categories that you create to inventory the people, places, and things in your business.</Description>
        <CatalogSection>
          {types.length < 1 ? (
            <Placeholder height="6rem">There are no catalog types yet.</Placeholder>
          ) : types.sort((a, b) => a.name.localeCompare(b.name)).map(type => (
            <Chip key={type.id} onClick={() => this.openTypeDialog(type)}>{type.name}</Chip>
          ))}
        </CatalogSection>
        {types.length > 0 ? (
          <>
            <SubHeader options={<Button onClick={() => this.openItemDialog()}>Add an Item</Button>}>Catalog Tree</SubHeader>
            <Description>The catalog tree shows you a hierarchical view of the people, places, and things in your business. You can group items together for better insights in your reports.</Description>
            <FilterToolbar
              start={[
                <Input
                  id="catalog-attribute-filter"
                  label="Filter:"
                  labelPosition="start"
                  type="select"
                  value={attributesOnly ? 'withAttributes' : 'all'}
                  onChange={this.handleAttributesOnlyChange}
                >
                  <option value="all">All items</option>
                  <option value="withAttributes">Items with attributes</option>
                </Input>,
                <SearchInput
                  id="catalog-search"
                  placeholder="Search Catalog..."
                  value={searchTerm}
                  onChange={this.handleSearchChange}
                />
              ]}
            />
            <CatalogSection>
              {displayTree.length < 1 ? (
                <Placeholder height="6rem">
                  {this.isFiltering()
                    ? "No catalog items match your filters."
                    : "There are no catalog items yet."}
                </Placeholder>
              ) : (
              <Tree
                treeData={displayTree}
                onChange={this.handleChangeTree}
                onVisibilityToggle={this.handleExpandCollapse}
                rowHeight={80}
                isVirtualized={false}
                generateNodeProps={({node}) => ({
                  buttons: [
                    <Button blank block onClick={() => this.deleteItem(node)}>
                      <Icon name="trash-2" />
                    </Button>,
                    <Button blank block onClick={() => this.openItemDialog(node)}>
                      <Icon name="edit" />
                    </Button>,
                    <Button blank block onClick={() => this.openItemDialog(null, node)}>
                      <Icon name="plus" />
                    </Button>,
                    <React.Fragment>
                      <Button blank block id={`copy-${node.id}`} title="Copy"
                        disabled={!canCopy}
                        onClick={() => node.children && node.children.length > 0
                          ? this.toggleCopyMenu(node.id)
                          : this.copyItem(node)}
                      >
                        <Icon name="copy" />
                      </Button>
                      {node.children && node.children.length > 0 && (
                        <Menu
                          open={copyMenuNodeId === node.id}
                          position="bottom-end"
                          relativeTo={`copy-${node.id}`}
                          onClose={this.closeCopyMenu}
                        >
                          <Menu.Option onClick={() => this.copyItem(node)}>Copy item</Menu.Option>
                          <Menu.Option onClick={() => this.handleCopyTree(node)}>Copy item and children</Menu.Option>
                        </Menu>
                      )}
                    </React.Fragment>,
                    <Button blank block onClick={() => this.openMetadataDialog(node)}>
                      <Icon name="list" color={hasAttributes(node) ? '#0049ee' : undefined} />
                    </Button>
                  ]
                })}
              />
              )}
            </CatalogSection>
          </>
        ) : null}
        <CatalogItemForm
          types={types}
          item={catalogDialog.item}
          parent={catalogDialog.parent}
          open={catalogDialog.open}
          onClose={this.closeItemDialog}
          onSave={this.handleSave}
        />
        <CatalogTypeForm
          types={types}
          type={typeDialog.type}
          open={typeDialog.open}
          onClose={this.closeTypeDialog}
          onSave={this.handleSaveType}
        />
        <CatalogMetadataForm
          item={metadataDialog.item}
          open={metadataDialog.open}
          onClose={this.closeMetadataDialog}
          onSave={this.handleSaveMetadata}
        />
      </Content>
    );
  }

  render() {
    return (
      <Screen title="Catalog" activeLink="Catalog">
        {this.renderCatalog()}
      </Screen>
    );
  }
}

const CatalogSection = styled.div`
  margin: 1.5rem 0 3rem 0;
`;

const FilterToolbar = styled(Card.Toolbar)`
  margin-top: 1rem;
  border: 0.0625rem solid #e4e4e4;
  border-radius: 0.625rem;
`;

const SearchInput = styled(props => <Input type="text" {...props} />)`
  width: 22rem;

  @media (max-width: 50rem) {
    width: auto;
  }
`;

const Description = styled.p`
  margin: 0.5rem 0 0 0;
  color: #747474;
  line-height: 1.5;
`;

const Chip = styled(props => <Button blank {...props} />)`
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  background: #0049ee;
  color: #fff;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
`;

function getChildren(item, catalog) {
  return catalog.filter(child => child.parentId === item.id);
}

function hasAttributes(item) {
  return Array.isArray(item.catalogMeta) && item.catalogMeta.length > 0;
}

function normalizeTree(tree, parentId = null) {
  return {
    ...tree,
    parentId,
    children: tree.children.map(child => normalizeTree(child, tree.id))
  };
}

function flattenTree(tree) {
  return tree
    .map(({children, ...item}) => {
      if(children.length < 1) {
        return [item];
      }

      return [item, ...flattenTree(children)];
    })
    .reduce((flatMap, next) => flatMap.concat(next), []);
}

export default withUser(CatalogScreen);
